/**
 * StructureTreePanel - Right-hand panel displaying molecular structure trees.
 *
 * Displays one or two ProgressiveHTMLTree components (reactant and product)
 * based on templateMode. Uses progressive disclosure style where short names
 * are shown in summaries, and full descriptions are revealed on expansion.
 */
import { useMemo, useState } from 'react';
import { fromConfigString } from '@alchemiesolns/workspace-serialization';
import {
  buildStructure,
  buildAltTextMapFromStructure,
  type SVGStructure,
  type TreeLabelMap,
} from '@alchemiesolns/svg-interactive';
import { extractWorkspaceFeatures } from '@alchemiesolns/feature-extractor-compat';
import { ProgressiveHTMLTree } from './ProgressiveHTMLTree';
import { ChevronRightIcon } from '@alchemiesolns/ui';

export interface StructureTreePanelProps {
  reactantConfig: string;
  productConfig: string;
  templateMode: 'reaction' | 'molecule';
  arrowType?: 'single' | 'double';
  topText?: string;
  bottomText?: string;
}

interface MoleculeTreeData {
  structure: SVGStructure;
  treeLabelMap: TreeLabelMap;
}

/**
 * Build the structure tree + label map for a config string. Uses the unified
 * `buildAltTextMapFromStructure` so RSG's tree shows the same names and
 * descriptions as Example 18 and the in-SVG accessibility tree.
 */
function buildTreeData(config: string): MoleculeTreeData | null {
  if (!config) return null;

  try {
    const workspace = fromConfigString(config);
    if (!workspace) return null;

    const featureResult = extractWorkspaceFeatures(workspace);
    if (!featureResult) return null;

    const structure = buildStructure(workspace, featureResult);
    if (!structure) return null;

    const treeLabelMap = buildAltTextMapFromStructure(structure, featureResult, workspace);

    return { structure, treeLabelMap };
  } catch (e) {
    console.warn('Failed to build tree data:', e);
    return null;
  }
}

/**
 * StructureTreePanel - Displays molecular structure trees in a right-hand panel.
 */
export function StructureTreePanel({
  reactantConfig,
  productConfig,
  templateMode,
  arrowType = 'single',
  topText = '',
  bottomText = '',
}: StructureTreePanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Build tree data for reactant
  const reactantData = useMemo(
    () => buildTreeData(reactantConfig),
    [reactantConfig]
  );

  // Build tree data for product (only in reaction mode)
  const productData = useMemo(
    () => (templateMode === 'reaction' ? buildTreeData(productConfig) : null),
    [productConfig, templateMode]
  );

  const hasContent = reactantData || productData;

  return (
    <aside className="figma-right-panel">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        aria-controls="structure-tree-panel-content"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: '100%',
          margin: '0',
          textAlign: 'left',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          color: '#374151'
        }}
      >
        <ChevronRightIcon
          width={16}
          height={16}
          style={{
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            flexShrink: 0,
          }}
        />
        Structure Tree
      </button>

      {!isExpanded ? null : (
        <div id="structure-tree-panel-content" style={{ paddingTop: '12px' }}>
          {!hasContent ? (
            <div style={{ color: '#666', fontStyle: 'italic' }}>
              No structure data available
            </div>
          ) : (
            templateMode === 'reaction' ? (
              <>
                {/* Reactant section */}
                {reactantData && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4
                      style={{
                        margin: '0 0 8px 0',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Reactant
                    </h4>
                    <ProgressiveHTMLTree
                      structure={reactantData.structure}
                      treeLabelMap={reactantData.treeLabelMap}
                      ariaLabel="Reactant molecule structure"
                    />
                  </div>
                )}

                {/* Arrow description */}
                {(topText || bottomText || arrowType) && (
                  <div
                    style={{
                      margin: '12px 0',
                      padding: '8px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '4px',
                      fontSize: '13px',
                      color: '#4b5563',
                    }}
                  >
                    {arrowType === 'double' ? 'Resonance arrow' : 'Reaction arrow'}
                    {topText && `: ${topText}`}
                    {bottomText && ` (${bottomText})`}
                  </div>
                )}

                {/* Product section */}
                {productData && (
                  <div>
                    <h4
                      style={{
                        margin: '0 0 8px 0',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Product
                    </h4>
                    <ProgressiveHTMLTree
                      structure={productData.structure}
                      treeLabelMap={productData.treeLabelMap}
                      ariaLabel="Product molecule structure"
                    />
                  </div>
                )}
              </>
            ) : (
              /* Molecule mode - single molecule */
              reactantData && (
                <ProgressiveHTMLTree
                  structure={reactantData.structure}
                  treeLabelMap={reactantData.treeLabelMap}
                  ariaLabel="Molecule structure"
                />
              )
            )
          )}
        </div>
      )}

    </aside>
  );
}
